import { useEffect, useMemo, useRef, useState } from 'react';

import SelectOption from '../option/SelectOption';
import PathfinderMultiSelectCardGrid from '../option/PathfinderMultiSelectCardGrid';
import PathfinderQuestionHeader from '../layout/PathfinderQuestionHeader';
import PathfinderBottomToolbar from '../layout/PathfinderBottomToolbar';
import PathfinderDropdownSelectBar from '../dropdown/PathfinderDropdownSelectBar';
import PathfinderSingleSelectOption from '../option/PathfinderSingleSelectOption';

import { ReactComponent as BackpackIcon } from '@repo/common/assets/icons/32px/icon_32px_backpack_outline.svg';

const EXPERIENCE_DURATION_OPTIONS = [
  { value: 1, label: '1 month' },
  { value: 3, label: '2-5 months' },
  { value: 6, label: '6-11 months' },
  { value: 12, label: '12-23 months' },
  { value: 24, label: '24-35 months' },
  { value: 36, label: '36 months or more' },
];

const EXPERIENCE_DURATION_VALUE_SET = new Set(
  EXPERIENCE_DURATION_OPTIONS.map((option) => option.value),
);

const normalizeDurations = (durations = {}, validCodes = []) =>
  Object.fromEntries(
    Object.entries(durations).filter(([code, months]) => {
      if (!validCodes.includes(code)) return false;

      const normalizedMonths = Number(months);
      return EXPERIENCE_DURATION_VALUE_SET.has(normalizedMonths);
    }),
  );

const deriveInitialDurations = (value, validCodes) => {
  if (value?.step2Durations && typeof value.step2Durations === 'object') {
    return normalizeDurations(value.step2Durations, validCodes);
  }

  if (!Array.isArray(value?.experiences)) {
    return {};
  }

  return Object.fromEntries(
    value.experiences
      .filter((experience) => validCodes.includes(experience?.expScope))
      .map((experience) => [experience.expScope, Number(experience.expMonths)])
      .filter(([, months]) => EXPERIENCE_DURATION_VALUE_SET.has(months)),
  );
};

const findFirstIncompleteDurationCode = (codes = [], durations = {}) =>
  codes.find(
    (code) => !EXPERIENCE_DURATION_VALUE_SET.has(Number(durations[code])),
  ) ?? null;

function ExperienceDurationDropdown({
  options = EXPERIENCE_DURATION_OPTIONS,
  value,
  placeholder = 'Select duration',
  open = false,
  onOpenChange,
  onChange,
}) {
  const rootRef = useRef(null);
  const selectedOption = options.find(
    (option) => option.value === Number(value),
  );
  const hasSelectedValue = Boolean(selectedOption);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        onOpenChange?.(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [onOpenChange, open]);

  return (
    <div ref={rootRef} className='relative flex flex-1 flex-col gap-[6px]'>
      <PathfinderDropdownSelectBar
        value={selectedOption?.label ?? ''}
        placeholder={placeholder}
        state={open ? 'active' : hasSelectedValue ? 'selected' : 'default'}
        onClick={() => onOpenChange?.(!open)}
        className='max-w-none w-full'
      />

      {open ? (
        <div className='absolute left-0 right-0 top-full z-20 overflow-hidden rounded-[15px] border border-grayscale-gray01 bg-grayscale-gray00 p-2 shadow-popup-02'>
          <div role='listbox' className='flex flex-col gap-0'>
            {options.map((option) => {
              const isSelected = option.value === Number(value);

              return (
                <PathfinderSingleSelectOption
                  key={option.value}
                  label={option.label}
                  state={isSelected ? 'selected' : 'default'}
                  reserveCheckSlot
                  className='w-full'
                  onClick={() => onChange?.(option.value)}
                />
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SelectedExperienceRow({
  title,
  durationValue,
  dropdownOpen,
  onToggleDropdown,
  onDurationChange,
}) {
  return (
    <div className='flex w-full items-start gap-4'>
      <div className='flex h-[76px] min-w-0 flex-1 items-center gap-2 rounded-[15px] border-2 border-surface-green40 bg-green-light01 p-4'>
        <div className='flex shrink-0 items-center px-2'>
          <BackpackIcon className='size-8 text-green-deep02' />
        </div>
        <span className='min-w-0 flex-1 text-left text-base font-medium leading-[1.5] text-green-deep02'>
          {title}
        </span>
      </div>

      <ExperienceDurationDropdown
        value={durationValue}
        open={dropdownOpen}
        onOpenChange={onToggleDropdown}
        onChange={onDurationChange}
      />
    </div>
  );
}

function PathfinderExpBlockFlow({
  value,
  onChange,
  onComplete,
  onBack,
  config = {},
  options = [],
  progressLabel,
  loading = false,
  skipDurationStep = false,
}) {
  const {
    step1Title = 'What is your highest level of education?',
    step1Helper = 'Select the option that best describes your background',
    step2Title = 'Which types of experience related to your target role do you have?',
    step2Helper = 'Select all that apply',
    step1Options: step1Codes = [],
    step2Options: step2Codes = [],
    step2ExclusiveOption = 'EXP_NONE_TYPE',
  } = config;

  const step1Options = useMemo(
    () =>
      options
        .filter((option) => step1Codes.includes(option.optionCode))
        .sort(
          (left, right) =>
            step1Codes.indexOf(left.optionCode) -
            step1Codes.indexOf(right.optionCode),
        )
        .map((option) => ({
          id: option.optionCode,
          title: option.optionLabel,
          caption: option.description || '',
          expScope: option.optionValue || option.optionCode,
        })),
    [options, step1Codes],
  );

  const step2Options = useMemo(
    () =>
      options
        .filter((option) => step2Codes.includes(option.optionCode))
        .sort(
          (left, right) =>
            step2Codes.indexOf(left.optionCode) -
            step2Codes.indexOf(right.optionCode),
        )
        .map((option) => ({
          id: option.optionCode,
          title: option.optionLabel,
          caption: option.description || '',
          expScope: option.optionValue || option.optionCode,
        })),
    [options, step2Codes],
  );

  const step2OptionMap = useMemo(
    () => new Map(step2Options.map((option) => [option.id, option])),
    [step2Options],
  );

  const orderedStep2Values = useMemo(() => {
    const selectedValues = Array.isArray(value?.step2) ? value.step2 : [];
    const selectedSet = new Set(selectedValues);

    return step2Options
      .map((option) => option.id)
      .filter((optionId) => selectedSet.has(optionId));
  }, [step2Options, value?.step2]);

  const initialStep2Durations = useMemo(
    () =>
      deriveInitialDurations(
        value,
        step2Options
          .map((option) => option.id)
          .filter((optionId) => optionId !== step2ExclusiveOption),
      ),
    [step2ExclusiveOption, step2Options, value],
  );

  const deriveInitialSubStep = () => {
    if (!value?.step1) return 1;
    if (orderedStep2Values.length === 0) return 2;
    if (orderedStep2Values.includes(step2ExclusiveOption)) return 2;
    if (skipDurationStep) return 2;
    if (value?.complete || Object.keys(initialStep2Durations).length > 0)
      return 3;
    return 2;
  };

  const [subStep, setSubStep] = useState(deriveInitialSubStep);
  const [step1Val, setStep1Val] = useState(value?.step1 || null);
  const [step2Vals, setStep2Vals] = useState(orderedStep2Values);
  const [step2Durations, setStep2Durations] = useState(initialStep2Durations);
  const [openDurationCode, setOpenDurationCode] = useState(() =>
    deriveInitialSubStep() === 3
      ? findFirstIncompleteDurationCode(
          orderedStep2Values.filter((code) => code !== step2ExclusiveOption),
          initialStep2Durations,
        )
      : null,
  );

  const selectedExperienceCodes = useMemo(
    () => step2Vals.filter((code) => code !== step2ExclusiveOption),
    [step2ExclusiveOption, step2Vals],
  );

  const isDurationSelected = (durationValue) =>
    EXPERIENCE_DURATION_VALUE_SET.has(Number(durationValue));

  const buildExperiences = (
    selectedCodes = step2Vals,
    durations = step2Durations,
  ) =>
    selectedCodes
      .filter((code) => code !== step2ExclusiveOption)
      .map((code, index) => {
        const option = step2OptionMap.get(code);
        return {
          expScope: option?.expScope ?? code,
          expRelevant: true,
          expMonths: isDurationSelected(durations[code])
            ? Number(durations[code])
            : null,
          displayOrder: index,
        };
      });

  const buildVal = (
    selectedCodes = step2Vals,
    durations = step2Durations,
    complete = false,
  ) => ({
    step1: step1Val,
    step2: selectedCodes,
    step2Durations: durations,
    complete,
    experiences: selectedCodes.includes(step2ExclusiveOption)
      ? []
      : skipDurationStep
        ? []
        : buildExperiences(selectedCodes, durations),
  });

  const handleStep1Next = () => {
    setSubStep(2);
  };

  const handleStep2Change = (ids) => {
    const orderedIds = step2Options
      .map((option) => option.id)
      .filter((optionId) => ids.includes(optionId));
    const nextDurations = normalizeDurations(step2Durations, orderedIds);

    setStep2Vals(orderedIds);
    setStep2Durations(nextDurations);
    onChange?.(buildVal(orderedIds, nextDurations, false));
  };

  const handleStep2Next = () => {
    if (step2Vals.length === 0) return;

    if (step2Vals.includes(step2ExclusiveOption) || skipDurationStep) {
      onComplete?.(buildVal(step2Vals, step2Durations, true));
      return;
    }

    setOpenDurationCode(
      findFirstIncompleteDurationCode(selectedExperienceCodes, step2Durations),
    );
    setSubStep(3);
  };

  const handleStep3Next = () => {
    if (!allDurationsComplete) return;
    onComplete?.(buildVal(step2Vals, step2Durations, true));
  };

  const handleDurationChange = (code, durationValue) => {
    const nextDurations = {
      ...step2Durations,
      [code]: durationValue,
    };

    setStep2Durations(nextDurations);
    onChange?.(buildVal(step2Vals, nextDurations, false));

    const nextIncompleteCode = selectedExperienceCodes.find(
      (selectedCode) =>
        selectedCode !== code &&
        !isDurationSelected(nextDurations[selectedCode]),
    );

    setOpenDurationCode(nextIncompleteCode ?? null);
  };

  const handleBack = () => {
    if (subStep === 1) {
      onBack?.();
      return;
    }

    setSubStep((currentStep) => currentStep - 1);
  };

  const allDurationsComplete =
    selectedExperienceCodes.length > 0 &&
    selectedExperienceCodes.every((code) =>
      isDurationSelected(step2Durations[code]),
    );

  const title = subStep === 1 ? step1Title : step2Title;
  const helperText = subStep === 1 ? step1Helper : step2Helper;

  const nextDisabled =
    loading ||
    (subStep === 1
      ? !step1Val
      : subStep === 2
        ? step2Vals.length === 0
        : !allDurationsComplete);

  return (
    <div className='flex min-h-[calc(100vh-74px)] flex-col bg-grayscale-gray00'>
      <div className='flex flex-1 items-center justify-center px-20 py-20'>
        <div className='flex w-full max-w-[868px] flex-1 flex-col items-start justify-center self-stretch overflow-visible'>
          <PathfinderQuestionHeader
            progressLabel={progressLabel}
            title={title}
            helperText={helperText}
          />

          {subStep === 1 && (
            <SelectOption
              options={step1Options}
              value={step1Val}
              onChange={(id) => setStep1Val(id)}
            />
          )}

          {subStep === 2 && (
            <PathfinderMultiSelectCardGrid
              options={step2Options}
              value={step2Vals}
              onChange={handleStep2Change}
              exclusiveOptionCode={step2ExclusiveOption}
            />
          )}

          {!skipDurationStep && subStep === 3 && (
            <div className='flex w-full flex-col gap-4'>
              {selectedExperienceCodes.map((code) => {
                const selectedOption = step2OptionMap.get(code);

                if (!selectedOption) return null;

                return (
                  <SelectedExperienceRow
                    key={code}
                    title={selectedOption.title}
                    durationValue={step2Durations[code]}
                    dropdownOpen={openDurationCode === code}
                    onToggleDropdown={(nextOpen) =>
                      setOpenDurationCode(nextOpen ? code : null)
                    }
                    onDurationChange={(durationValue) =>
                      handleDurationChange(code, durationValue)
                    }
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>

      <PathfinderBottomToolbar
        onBack={handleBack}
        onNext={
          subStep === 1
            ? handleStep1Next
            : subStep === 2
              ? handleStep2Next
              : handleStep3Next
        }
        nextLabel='Next'
        nextDisabled={nextDisabled}
      />
    </div>
  );
}

export default PathfinderExpBlockFlow;
